import React, { createContext, Dispatch, MutableRefObject, ReactNode, SetStateAction, useContext, useMemo, useRef } from 'react';
import {AuthContext} from '../AuthContext';
import useDashboardEffects from '../../hooks/context/effects/useDashboardEffects';
import useDashboardContext from '../../hooks/context/useDashboardContext';
import { Dashboard, DashboardQuery, DashboardQuerySource } from '../../types/files';
import { AnswerQuestionResponse } from '../../types/search';

export type AnsweredQuestion = AnswerQuestionResponse & { question: string, time: Date };
export type DashboardQueryAnswer = DashboardQuery & {answeredQuestion: AnsweredQuestion};

export type DefaultQueryTemplate = {
  isPrimaryQuery: boolean,
  title: string,
  displayTitle: string,
  queryTemplate: string,
  source: DashboardQuerySource,
  order: number,
  visibleByDefault: boolean,
};

type DashboardContextProps = {
  dashboard: Dashboard | null;
  setDashboard: Dispatch<SetStateAction<Dashboard | null>>;
  dashboardQueries: DashboardQuery[];
  dashboardQueriesRef: MutableRefObject<DashboardQuery[]>;
  setDashboardQueries: Dispatch<SetStateAction<DashboardQuery[]>>;
  defaultQueryTemplateValues: DefaultQueryTemplate[];
  setDefaultQueryTemplateValues: Dispatch<SetStateAction<DefaultQueryTemplate[]>>;
  isPublicView: boolean;
  setPublicView: Dispatch<SetStateAction<boolean>>;
  isShared: boolean;
  setShared: Dispatch<SetStateAction<boolean>>;
}

export const DashboardContext = createContext<DashboardContextProps>({
  dashboard: null,
  setDashboard: () => {},
  dashboardQueries: [],
  dashboardQueriesRef: { current: [] },
  setDashboardQueries: () => {},
  defaultQueryTemplateValues: [],
  setDefaultQueryTemplateValues: () => {},
  isPublicView: false,
  setPublicView: () => {},
  isShared: false,
  setShared: () => {},
});

const DashboardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userGroup } = useContext(AuthContext);
  const {
    dashboard,
    dashboardQueries,
    defaultQueryTemplateValues,
    isPublicView,
    isShared,
    setDashboard,
    setDashboardQueries,
    setDefaultQueryTemplateValues,
    setPublicView,
    setShared,
  } = useDashboardContext();
  const dashboardQueriesRef = useRef<DashboardQuery[]>([]);
  useDashboardEffects(userGroup, dashboardQueriesRef);

  const contextValue = useMemo(() => ({
    dashboard,
    dashboardQueries,
    dashboardQueriesRef,
    defaultQueryTemplateValues,
    isPublicView,
    isShared,
    setDashboard,
    setDashboardQueries,
    setDefaultQueryTemplateValues,
    setPublicView,
    setShared,
  }), [
    dashboard,
    dashboardQueries,
    dashboardQueriesRef,
    defaultQueryTemplateValues,
    isPublicView,
    isShared,
    setDashboard,
    setDashboardQueries,
    setDefaultQueryTemplateValues,
    setPublicView,
    setShared,
  ]);

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardProvider;
